/**
 * The page masthead of the approved prototype (style.css `.masthead`): a paper band bleeding to the content edges
 * under the mode band, with the page title, an intro line, an optional date / cycle block on the right and an
 * optional scope row under a hairline. Pages compose it from these primitives; styles are in styles/ipd.css.
 *
 *   <Masthead title="Today" intro={<MastheadCounts parts={[[1, "assignment action"], [6, "review items"]]} tail="ready for your attention" />}
 *     aside={<><CalendarDate date={now} /><CycleTag label="2026 H1" state="Draft cycle" /></>}
 *     scope={<MastheadScope who="Bill Marino's work">…</MastheadScope>} />
 */
import { Fragment, type ReactNode } from "react";
import { LineIcon } from "./LineIcon.tsx";

export function Masthead({ title, titleId, intro, aside, scope, children }: {
  readonly title: ReactNode; readonly titleId?: string; readonly intro?: ReactNode; readonly aside?: ReactNode; readonly scope?: ReactNode; readonly children?: ReactNode;
}) {
  return (
    <header className="ipd-masthead">
      <div className="ipd-masthead-heading">
        <div>
          <h1 id={titleId}>{title}</h1>
          {intro ? <div className="ipd-masthead-intro">{intro}</div> : null}
        </div>
        {aside ? <div className="ipd-masthead-date">{aside}</div> : null}
      </div>
      {scope}
      {children}
    </header>
  );
}

/** The structured intro: "**1** assignment action | **6** review items · ready for your attention". A null count shows "—". */
export function MastheadCounts({ parts, tail }: { readonly parts: readonly (readonly [number | null, string])[]; readonly tail?: ReactNode }) {
  return (
    <>
      {parts.map(([n, label], i) => (
        <Fragment key={label}>
          {i > 0 ? <span className="ipd-masthead-divider" aria-hidden="true" /> : null}
          <span><strong className="ipd-num">{n ?? "—"}</strong> {label}</span>
        </Fragment>
      ))}
      {tail ? <span className="ipd-masthead-tail">{tail}</span> : null}
    </>
  );
}

/** "30 September / Wednesday", in the viewer's locale and time zone unless one is given. */
export function CalendarDate({ date, timeZone }: { readonly date: Date; readonly timeZone?: string }) {
  const part = (o: Intl.DateTimeFormatOptions) => date.toLocaleDateString("en-US", { ...o, timeZone });
  return (
    <div className="ipd-calendar" role="img" aria-label={part({ weekday: "long", month: "long", day: "numeric" })}>
      <span className="ipd-calendar-day" aria-hidden="true">{part({ day: "numeric" })}</span>
      <span className="ipd-calendar-month" aria-hidden="true">{part({ month: "long" })}<span>{part({ weekday: "long" })}</span></span>
    </div>
  );
}

/** The left-ruled cycle tag: "2026 H1" over a state chip ("Draft cycle"). */
export function CycleTag({ label, state }: { readonly label: ReactNode; readonly state?: ReactNode }) {
  return <span className="ipd-cycle-tag">{label}{state ? <b>{state}</b> : null}</span>;
}

/** The scope row: people icon + "{Name}'s work" (or a picker in its place), then the page's controls. Put
 * `className="ipd-masthead-push"` on the child that should sit at the right (the prototype's sync status). */
export function MastheadScope({ who, children }: { readonly who: ReactNode; readonly children?: ReactNode }) {
  return (
    <div className="ipd-masthead-scope">
      <span className="ipd-masthead-who"><LineIcon name="people" />{who}</span>
      {children}
    </div>
  );
}
